import { type MutableRefObject, useCallback, useEffect } from 'react';

import { usePreviousCompare } from '../use-previous-compare';

export interface FlashOptions<T> {
  compare: (previous: T | null, next: T | null) => boolean;
  flashingOpacity: number;
  initialOpacity: number;
  opacityCssVar: string;
}
export interface UseFlashOptions<T> {
  options?: Partial<FlashOptions<T>>;
  ref: MutableRefObject<HTMLElement | null>;
  trigger: T;
}

const defaultComparer = <T>(previous: T, next: T): boolean => previous !== next;

export const defaultFlashOptions = {
  compare: defaultComparer,
  flashingOpacity: 1,
  initialOpacity: 0.4,
  opacityCssVar: '--oc-background-opacity',
};

export const useFlash = <T>({ options = defaultFlashOptions, ref, trigger }: UseFlashOptions<T>): void => {
  const { compare, flashingOpacity, initialOpacity, opacityCssVar } = {
    ...defaultFlashOptions,
    ...options,
  };

  const shouldFlash = usePreviousCompare<T>(trigger, compare);

  shouldFlash && ref.current?.style.setProperty(opacityCssVar, flashingOpacity.toString());

  const resetTransition = useCallback(() => {
    const el = ref.current;

    if (el?.style.getPropertyValue(opacityCssVar) !== initialOpacity.toString()) {
      el?.style.setProperty(opacityCssVar, initialOpacity.toString());
    }
  }, [ref, initialOpacity, opacityCssVar]);

  useEffect(() => {
    const el = ref.current;
    el?.addEventListener('transitionend', resetTransition);

    return () => {
      el?.removeEventListener('transitionend', resetTransition);
    };
  }, [ref, resetTransition]);
};
